import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { PromptContext, type PromptOptions } from './PromptContext';

interface Pending extends PromptOptions {
  resolve: (v: string | null) => void;
}

/**
 * In-app text prompt in the ERP theme. Replaces window.prompt(), which looks
 * out of place and isn't supported in some embedded browsers.
 */
export function PromptProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const fieldRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  const ask = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setValue(opts.defaultValue ?? '');
        setError('');
        setPending({ ...opts, resolve });
      }),
    [],
  );

  const close = useCallback(
    (result: string | null) => {
      pending?.resolve(result);
      setPending(null);
    },
    [pending],
  );

  function submit() {
    if (pending?.input !== false && (pending?.required ?? true) && !value.trim()) {
      setError(`${pending?.label ?? 'This field'} is required`);
      fieldRef.current?.focus();
      return;
    }
    close(value);
  }

  // Escape cancels the prompt only — captured first so a modal underneath
  // doesn't close too.
  useEffect(() => {
    if (!pending) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close(null);
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [pending, close]);

  const okRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (pending) setTimeout(() => (pending.input === false ? okRef.current : fieldRef.current)?.focus(), 0);
  }, [pending]);

  const required = pending?.required ?? true;

  return (
    <PromptContext.Provider value={ask}>
      {children}
      {pending && (
        <div
          className="fixed inset-0 z-[600] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm"
          onMouseDown={(e) => e.target === e.currentTarget && close(null)}
        >
          <form
            role="dialog"
            aria-modal="true"
            className={`w-full ${pending.wide ? 'max-w-2xl' : 'max-w-md'} overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--modal-bg)] shadow-2xl`}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className="border-b border-[var(--border-color)] px-5 py-3 text-base font-extrabold text-[var(--text-primary)]">
              {pending.title}
            </div>
            {(pending.message || pending.input !== false) && (
            <div className="space-y-3 px-5 py-4">
              {pending.message && (
                <div className="whitespace-pre-line text-[13px] leading-relaxed text-[var(--text-secondary)]">{pending.message}</div>
              )}
              {pending.input !== false && (
              <FormGroup label={pending.label ?? 'Value'} required={required} error={error || undefined}>
                {pending.multiline ? (
                  <Textarea
                    ref={fieldRef}
                    rows={3}
                    value={value}
                    invalid={!!error}
                    placeholder={pending.placeholder}
                    onChange={(e) => { setValue(e.target.value); setError(''); }}
                    onKeyDown={(e) => {
                      // Ctrl/⌘+Enter submits from the textarea.
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
                    }}
                  />
                ) : (
                  <Input
                    ref={fieldRef}
                    value={value}
                    invalid={!!error}
                    inputMode={pending.inputMode}
                    placeholder={pending.placeholder}
                    onChange={(e) => { setValue(e.target.value); setError(''); }}
                  />
                )}
              </FormGroup>
              )}
            </div>
            )}
            <div className="flex justify-end gap-2 border-t border-[var(--border-color)] px-5 py-3">
              <Button variant="glass" onClick={() => close(null)}>
                Cancel
              </Button>
              <Button ref={okRef} type="submit" variant={pending.tone === 'danger' ? 'danger' : 'primary'}>
                {pending.confirmLabel ?? 'OK'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </PromptContext.Provider>
  );
}
